import dayjs from '~/timeInit';

import { useGetOfferPackages } from '~/hooks/apis/offers/useGetOfferPackages';
import { useGetSurchargeMargins } from '~/hooks/apis/reservation/useGetSurchargeMargins';

import { HotelBundleComponent } from '~/types/bundles/bundle';

/**
 * What we keep on the hotel stay for each check-in, in whole AUD before any bundle adjustment: the package's
 * margin (svc-offer's margin_percentage, which counts complimentary addons and bundled experiences) on the
 * stay's base price, plus the margin on any date surcharges the stay picks up. Undefined when the package has
 * no cost to work from.
 */
export default function useHotelMargins(hotel: HotelBundleComponent | undefined, checkIns: Array<string>) {
  const { data: packages, isLoading: isLoadingPackages, error: packagesError } = useGetOfferPackages(hotel?.offer_id);
  // svc-offer returns a package per room rate; the calendar prices the first, so the margin does too.
  const pkg = packages?.find((item) => item.id_salesforce_external === hotel?.package_id) as
    | App.AccommodationPackage
    | undefined;
  const price = pkg?.prices.find((row) => row.currency_code === 'AUD');
  const marginPercentage = pkg?.margin_percentage;
  // svc-offer reports 100% when Salesforce has no cost, which is unknown rather than all margin.
  const hasCost = typeof marginPercentage === 'number' && marginPercentage < 100 && !!price;

  const stays = hotel
    ? checkIns.map((checkIn) => ({
        startDate: checkIn,
        endDate: dayjs(checkIn)
          .add(hotel.nights - 1, 'day')
          .format('YYYY-MM-DD'),
      }))
    : [];
  const surcharges = useGetSurchargeMargins(hasCost ? pkg?.fk_room_rate_id : undefined, stays);

  const extraNights = hotel && pkg ? Math.max(hotel.nights - (pkg.number_of_nights ?? hotel.nights), 0) : 0;
  const baseSell = price ? price.price + extraNights * (price.nightly_price ?? 0) : 0;
  const baseMargin = hasCost ? (baseSell * marginPercentage) / 100 : undefined;

  const marginOn = (checkIn: string): number | undefined => {
    const surchargeMargin = surcharges.byStartDate[checkIn];
    return baseMargin === undefined || surchargeMargin === undefined
      ? undefined
      : Math.round(baseMargin + surchargeMargin);
  };

  // OffersService.getOffer swallows request failures, so a failed load arrives as no packages, not an error.
  const missingPackage =
    hotel && !isLoadingPackages && !packagesError && !pkg
      ? new Error("The hotel's package couldn't be loaded")
      : undefined;

  return {
    marginOn,
    hasCost,
    isLoading: isLoadingPackages || surcharges.isLoading,
    error: packagesError ?? missingPackage ?? surcharges.error,
  };
}
